import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

export interface MonthlyRevenuePoint {
  mese: string       // "Gen", "Feb", ...
  corrente: number   // Revenue mese corrente
  precedente?: number // Revenue stesso mese anno prec.
}

interface RevenueChartProps {
  data: MonthlyRevenuePoint[]
}

function formatEurK(value: number): string {
  if (value >= 1_000_000) return `€${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `€${(value / 1_000).toFixed(0)}k`
  return `€${value.toFixed(0)}`
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ name: string; value: number; color: string }>
  label?: string
}) {
  if (!active || !payload?.length) return null

  return (
    <div className="rounded-xl border border-[--km-border] bg-[#141b28] px-4 py-3 shadow-xl text-sm">
      <p className="text-[--km-text-secondary] mb-2 font-medium">{label}</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center gap-2">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ background: entry.color }}
          />
          <span className="text-[--km-text-secondary] capitalize">{entry.name}:</span>
          <span className="text-[--km-text-primary] font-semibold tabular-nums">
            {formatEurK(entry.value)}
          </span>
        </div>
      ))}
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 py-12">
      <p className="text-sm text-[--km-text-muted]">Nessun dato di ricavo disponibile.</p>
      <p className="text-xs text-[--km-text-muted]">
        Carica i file di fatturazione per visualizzare il grafico.
      </p>
    </div>
  )
}

export function RevenueChart({ data }: RevenueChartProps) {
  const hasData = data.length > 0
  const hasPrevYear = data.some((d) => d.precedente !== undefined)

  return (
    <div className="h-full w-full">
      {!hasData ? (
        <EmptyState />
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="gradCurrentYear" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6366f1" stopOpacity={0.35} />
                <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradPrevYear" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#a855f7" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(255,255,255,0.06)"
              vertical={false}
            />

            <XAxis
              dataKey="mese"
              tick={{ fill: 'var(--km-text-muted)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              dy={8}
              interval="preserveStartEnd"
            />

            <YAxis
              tickFormatter={formatEurK}
              tick={{ fill: 'var(--km-text-muted)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={44}
            />

            <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.1)', strokeWidth: 1 }} />

            {hasPrevYear && (
              <Area
                type="monotone"
                dataKey="precedente"
                name="Anno prec."
                stroke="#a855f7"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                fill="url(#gradPrevYear)"
                dot={false}
                activeDot={{ r: 4, fill: '#a855f7', strokeWidth: 0 }}
              />
            )}

            <Area
              type="monotone"
              dataKey="corrente"
              name="Anno corrente"
              stroke="#6366f1"
              strokeWidth={2}
              fill="url(#gradCurrentYear)"
              dot={false}
              activeDot={{ r: 5, fill: '#6366f1', stroke: '#fff', strokeWidth: 1.5 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}
